import type { Action } from '@/core/input';
import {
  ASSIGNABLE_ACTIONS,
  BUTTON_SLOTS,
  type ButtonSlot,
  DEFAULT_SETTINGS,
  type Settings,
  assignSlot,
} from '@/core/settings';
import { VIEWS, type ViewName } from '@/core/viewport';
import { createSlotGlyph } from '@/ui/glyphs';
import { ACTION_NAMES, SLOT_APPEARANCE } from '@/ui/touchControls';

/**
 * Control remapping: pick a button, then pick what it does.
 *
 * The obvious alternative — tap a row to cycle its action — turns configuring
 * into whack-a-mole, because swapping to avoid duplicates displaces a second
 * button on every tap. Naming the target first means one deliberate change per
 * interaction, and the diamond shows the whole layout while you edit it.
 */

export interface SettingsPanelOptions {
  readonly container: HTMLElement;
  readonly initial: Settings;
  readonly onChange: (settings: Settings) => void;
}

export class SettingsPanel {
  private readonly root: HTMLDivElement;
  private readonly panel: HTMLDivElement;
  private readonly slotButtons = new Map<ButtonSlot, HTMLButtonElement>();
  private readonly actionButtons = new Map<Action, HTMLButtonElement>();
  private readonly viewButtons = new Map<ViewName, HTMLButtonElement>();
  private readonly onChange: (settings: Settings) => void;

  private settings: Settings;
  private selected: ButtonSlot = 'bottom';

  constructor({ container, initial, onChange }: SettingsPanelOptions) {
    this.settings = initial;
    this.onChange = onChange;

    this.root = document.createElement('div');
    this.root.className = 'settings-root';
    this.root.appendChild(this.createOpenButton());

    this.panel = document.createElement('div');
    this.panel.className = 'settings-panel';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-label', 'Controls');
    this.panel.append(this.createHeader(), this.createBody(), this.createFooter());

    this.root.appendChild(this.panel);
    container.appendChild(this.root);
    this.refresh();
  }

  setOpen(open: boolean): void {
    this.panel.hidden = !open;
    this.root.classList.toggle('is-open', open);
    document.body.classList.toggle('menu-open', open);
  }

  destroy(): void {
    this.root.remove();
  }

  private createOpenButton(): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'settings-open';
    button.type = 'button';
    button.textContent = '⚙';
    button.setAttribute('aria-label', 'Settings');
    button.addEventListener('click', () => {
      this.setOpen(true);
    });
    return button;
  }

  private createHeader(): HTMLElement {
    const header = document.createElement('div');
    header.className = 'settings-header';

    const heading = document.createElement('h2');
    heading.textContent = 'Controls';

    const hint = document.createElement('p');
    hint.className = 'settings-hint';
    hint.textContent = 'Pick a button, then pick what it does.';

    header.append(heading, hint);
    return header;
  }

  private createBody(): HTMLElement {
    const body = document.createElement('div');
    body.className = 'settings-body';

    const diamond = document.createElement('div');
    diamond.className = 'settings-diamond';
    diamond.setAttribute('role', 'radiogroup');
    diamond.setAttribute('aria-label', 'Button');
    for (const slot of BUTTON_SLOTS) {
      const button = this.createSlotButton(slot);
      this.slotButtons.set(slot, button);
      diamond.appendChild(button);
    }

    const list = document.createElement('div');
    list.className = 'settings-actions-list';
    list.setAttribute('role', 'radiogroup');
    list.setAttribute('aria-label', 'Action');
    for (const action of ASSIGNABLE_ACTIONS) {
      const button = this.createActionButton(action);
      this.actionButtons.set(action, button);
      list.appendChild(button);
    }

    body.append(diamond, list, this.createViewRow());
    return body;
  }

  /**
   * How much world is on screen.
   *
   * Lives with the controls because it is the same kind of preference: a
   * property of the device and the hands holding it, not of the playthrough.
   */
  private createViewRow(): HTMLElement {
    const row = document.createElement('div');
    row.className = 'settings-views';
    row.setAttribute('role', 'radiogroup');
    row.setAttribute('aria-label', 'View');

    const label = document.createElement('p');
    label.className = 'settings-views-label';
    label.textContent = 'View';
    row.appendChild(label);

    for (const [name, size] of Object.entries(VIEWS) as [ViewName, { height: number }][]) {
      const button = document.createElement('button');
      button.className = 'settings-view';
      button.type = 'button';
      button.setAttribute('role', 'radio');
      button.textContent = name === 'close' ? 'Close' : 'Wide';

      // Tiles tall, because that is what a view fixes — the width follows the
      // shape of the screen, so it is not a number worth promising.
      const tiles = document.createElement('span');
      tiles.className = 'settings-view-tiles';
      tiles.textContent = `${Math.round(size.height / 16)} tiles tall`;
      button.appendChild(tiles);

      button.addEventListener('click', () => {
        this.apply({ ...this.settings, view: name });
      });

      this.viewButtons.set(name, button);
      row.appendChild(button);
    }

    return row;
  }

  private createSlotButton(slot: ButtonSlot): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = `settings-slot settings-slot--${slot}`;
    button.type = 'button';
    button.setAttribute('role', 'radio');
    button.style.setProperty('--tint', SLOT_APPEARANCE[slot].tint);

    const glyph = document.createElement('span');
    glyph.className = 'settings-slot-glyph';
    glyph.appendChild(createSlotGlyph(slot, 20));

    const label = document.createElement('span');
    label.className = 'settings-slot-label';

    button.append(glyph, label);
    button.addEventListener('click', () => {
      this.selected = slot;
      this.refresh();
    });

    return button;
  }

  private createActionButton(action: Action): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'settings-action-option';
    button.type = 'button';
    button.setAttribute('role', 'radio');

    const name = document.createElement('span');
    name.textContent = ACTION_NAMES[action];

    const state = document.createElement('span');
    state.className = 'settings-action-state';

    button.append(name, state);
    button.addEventListener('click', () => {
      this.apply(assignSlot(this.settings, this.selected, action));
    });

    return button;
  }

  private createFooter(): HTMLElement {
    const footer = document.createElement('div');
    footer.className = 'settings-footer';

    const reset = document.createElement('button');
    reset.className = 'settings-secondary';
    reset.type = 'button';
    reset.textContent = 'Reset';
    reset.addEventListener('click', () => {
      this.apply(DEFAULT_SETTINGS);
    });

    const close = document.createElement('button');
    close.className = 'settings-primary';
    close.type = 'button';
    close.textContent = 'Done';
    close.addEventListener('click', () => {
      this.setOpen(false);
    });

    footer.append(reset, close);
    return footer;
  }

  private apply(settings: Settings): void {
    this.settings = settings;
    this.refresh();
    this.onChange(settings);
  }

  private refresh(): void {
    for (const [name, button] of this.viewButtons) {
      const isSelected = name === this.settings.view;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-checked', String(isSelected));
    }

    for (const [slot, button] of this.slotButtons) {
      const action = this.settings.slots[slot];
      const isSelected = slot === this.selected;

      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-checked', String(isSelected));

      const label = button.querySelector('.settings-slot-label');
      if (label) label.textContent = ACTION_NAMES[action];
      button.setAttribute('aria-label', `${ACTION_NAMES[action]} button`);
    }

    const current = this.settings.slots[this.selected];
    const tint = SLOT_APPEARANCE[this.selected].tint;

    for (const [action, button] of this.actionButtons) {
      const isCurrent = action === current;
      button.classList.toggle('is-current', isCurrent);
      button.setAttribute('aria-checked', String(isCurrent));
      button.style.setProperty('--tint', tint);

      // Naming the button that gives the action up makes the swap predictable
      // instead of something the player discovers after the fact.
      const holder = BUTTON_SLOTS.find((slot) => this.settings.slots[slot] === action);
      const state = button.querySelector('.settings-action-state');
      if (state) {
        state.textContent = isCurrent
          ? 'assigned'
          : holder
            ? `swaps with ${ACTION_NAMES[this.settings.slots[this.selected]]}`
            : '';
      }
    }
  }
}
